import { IconType, Heading, Button, Icon } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { NAMESPACE } from 'locale'
import { selfServeConstants } from 'locale/selfServe'
import { Text } from '@lmc/ui-kit/src/general'
import { useDispatch } from 'react-redux'
import { Home } from '@lmc/ts-common'
import { FC, useState } from 'react'
import { getJSON, postJSON } from 'common/services/api'
import * as GlobalActions from 'actions/Global'
import { LogoutButton } from './LogoutButton'
import { constants } from '@lmc/common'
import { Logo } from './Logo'

export type Props = {
  start: () => void
  home?: Home
}

export const StepDiscover: FC<Props> = ({ start, home }: Props) => {
  const { t } = useTranslation(NAMESPACE.SELF_SERVE)

  const dispatch = useDispatch()

  const hasPro = home && home?.features?.pro?.enabled

  const [isLoading, setIsLoading] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const subscribeTo = async () => {
    try {
      setIsLoading(true)
      await postJSON({
        url: 'services/chargebee/subscriptions/pro-monthly'
      })

      const res = await getJSON({ url: '/api/init' })

      const { home: homeRefresh } = res.data

      if (res) {
        dispatch(GlobalActions.setHomeDetails(homeRefresh))
        return
      }

      setIsLoading(false)
    } catch (error) {
      console.log(error)
    }
  }

  const isSelfServe = home?.source === 'Self-serve'

  const getButton = () => {
    if (isSelfServe) {
      return (
        <ButtonWrapper>
          <StyledButton
            variant="popout"
            onClick={subscribeTo}
            size="large"
            isLoading={isLoading}
          >
            {t(selfServeConstants.START_PRO_TRIAL)}
          </StyledButton>
        </ButtonWrapper>
      )
    }

    if (hasPro) {
      return (
        <ButtonWrapper>
          <StyledButton
            variant="popout"
            onClick={start}
            size="large"
            isLoading={isLoading}
          >
            {t(selfServeConstants.START_SET_UP)}
          </StyledButton>
        </ButtonWrapper>
      )
    }

    return (
      <ButtonWrapper>
        <StyledButton
          isLoading={isLoading}
          variant="popout"
          size="large"
          onClick={() => window.open(constants.HUBSPOT_DISCOVERY_CALL_URL)}
        >
          {t(selfServeConstants.INTERESTED_IN)}
        </StyledButton>
        <ContinueWrapper onClick={start}>
          <StyledText level={1}>
            {t(selfServeConstants.CONTINUE_WITH)}
          </StyledText>
        </ContinueWrapper>
      </ButtonWrapper>
    )
  }

  // Svg will be replace with png background image
  return (
    <>
      <Icon
        customIcon={IconType.CleanLargeLogoIcon}
        style={{
          height: '100vh',
          width: 1000,
          position: 'absolute',
          right: 0,
          top: '-20px',
          overflow: 'hidden'
        }}
      />
      <Wrapper>
        <TopWrapper>
          <Logo />
          <LogoutButton />
        </TopWrapper>
        <CenterWrapper>
          <StyledHeading level={1}>
            {t(selfServeConstants.DISCOVER_WHAT)}
          </StyledHeading>
          {getButton()}
        </CenterWrapper>
      </Wrapper>
    </>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 100px 160px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 10px;
  }
`

const CenterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: auto;
  margin-top: auto;
`

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 30px;
`

const ContinueWrapper = styled.div`
  z-index: 10;
  cursor: pointer;
`

const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

const StyledHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color11};
  font-weight: 800;
  font-size: 54px;
  max-width: 800px;
  font-family: gilroy;
  line-height: 59px;
`

const StyledButton = styled(Button)`
  width: max-content;
  margin-right: 20px;
  z-index: 10;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color11};
  text-decoration: underline;
`
